import { Transaction, ComputeBudgetProgram } from '@solana/web3.js';
import { getSDK as getSolanaSDK } from '../sdk/SolanaInstance';
import { Keypair } from '@solana/web3.js';
import { loadLocalKeys } from '../db/KeyStorageManager';
const COMPUTE_UNIT_LIMIT = 1_400_000;
const MAX_RETRIES = 3;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Sends Solana transaction instructions with optional random delay and retry logic.
 *
 * @param {Object} params
 * @param {TransactionInstruction[]} params.instructions
 * @param {bigint} [params.networkFeeLamports=0n]
 * @param {number} [params.randomDelayChance=0.6] - Chance (0–1) to delay before a tx
 * @param {number} [params.delayMinMs=800]
 * @param {number} [params.delayMaxMs=3000]
 * @returns {Promise<string[]>}
 */
export async function sendInstructionsWithRetry({
  instructions,
  networkFeeLamports = 0n,
  randomDelayChance = 0.6,
  delayMinMs = 800,
  delayMaxMs = 3000,
  password, 
  userId, 
  salt,
}) {
  const signatures = [];
  const transactions = [];
  const connection =  getSolanaSDK();
  const { fundingKeypair: fundingKeypair } = await loadLocalKeys({ userId, password, salt });

  // create full key
  for (let i = 0; i < instructions.length; i++) {
    const instruction = instructions[i];

    if (Math.random() < randomDelayChance) {
      await sleep(getRandomInt(delayMinMs, delayMaxMs));
    }

    let lastError = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const { blockhash } = await connection.getLatestBlockhash();

        const tx = new Transaction({
          recentBlockhash: blockhash,
          feePayer: fundingKeypair.publicKey,
        });

        tx.add(ComputeBudgetProgram.setComputeUnitLimit({ units: COMPUTE_UNIT_LIMIT }));

        if (networkFeeLamports > 0n) {
          const microLamports = Number((networkFeeLamports * 1_000_000n) / BigInt(COMPUTE_UNIT_LIMIT));
          tx.add(ComputeBudgetProgram.setComputeUnitPrice({ microLamports }));
        }

        tx.add(instruction);
        tx.sign(fundingKeypair);

        const signature = await connection.sendRawTransaction(tx.serialize(), {
          skipPreflight: false,
          preflightCommitment: 'confirmed',
        });

        signatures.push(signature);
        transactions.push(tx);
        break;
      } catch (err) {
        lastError = err;
        await sleep(getRandomInt(500, 1500));
      }
    }

    if (signatures.length <= i) {
      throw new Error(`Failed to send instruction at index ${i}: ${lastError}`);
    }
  }

  return {
    signatures,
    transactions,
  };
}